import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, apiUploadFiles, queryClient as globalQueryClient } from "@/lib/queryClient";
import {
  Palette,
  Loader2,
  Upload,
  Image as ImageIcon,
  Sparkles,
  RotateCcw,
  Check,
  AlertTriangle,
} from "lucide-react";

// Kept in sync with the app's shared/brandFonts.ts (PLAN.md section 1).
const BRAND_FONTS = [
  "Playfair Display",
  "Cormorant Garamond",
  "EB Garamond",
  "Libre Baskerville",
  "Open Sans",
  "Inter",
  "Lato",
  "Montserrat",
  "Jost",
  "Source Sans 3",
  "system",
];

const LOGO_PLACEMENT_LABELS: Record<string, string> = {
  all: "Auf allen Slides",
  first: "Nur erstes Slide",
  none: "Kein Logo",
};

const TEXT_COLORS = ["#ffffff", "#111111"] as const;

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ["image/png", "image/webp", "image/jpeg"];
const MAX_STYLE_NOTE = 400;
const MAX_REFERENCES = 6;

interface BrandKitColors {
  primary: string;
  secondary: string;
  text: "#ffffff" | "#111111";
}

interface BrandKitFonts {
  heading: string;
  body: string;
}

interface BrandKitDto {
  version: number;
  logoUrl: string | null;
  logoPlacement: "all" | "first" | "none";
  colors: BrandKitColors;
  fonts: BrandKitFonts;
  styleNote: string;
  referenceUploadIds: number[];
  updatedAt: string;
}

interface BrandResponse {
  enabled: boolean;
  brand: BrandKitDto | null;
  version: number;
  fonts: string[];
}

interface ApprovedUpload {
  id: number | string;
  filePath: string;
  message?: string | null;
}

interface Draft {
  logoPlacement: "all" | "first" | "none";
  colors: BrandKitColors;
  fonts: BrandKitFonts;
  styleNote: string;
  referenceUploadIds: (number | string)[];
}

const DEFAULT_DRAFT: Draft = {
  logoPlacement: "none",
  colors: { primary: "#1e252f", secondary: "#1e252f", text: "#ffffff" },
  fonts: { heading: "Playfair Display", body: "Open Sans" },
  styleNote: "",
  referenceUploadIds: [],
};

function dtoToDraft(brand: BrandKitDto | null): Draft {
  if (!brand) return { ...DEFAULT_DRAFT };
  return {
    logoPlacement: brand.logoPlacement,
    colors: { ...brand.colors },
    fonts: { ...brand.fonts },
    styleNote: brand.styleNote,
    referenceUploadIds: [...brand.referenceUploadIds],
  };
}

function draftsEqual(a: Draft, b: Draft): boolean {
  return (
    a.logoPlacement === b.logoPlacement &&
    a.colors.primary === b.colors.primary &&
    a.colors.secondary === b.colors.secondary &&
    a.colors.text === b.colors.text &&
    a.fonts.heading === b.fonts.heading &&
    a.fonts.body === b.fonts.body &&
    a.styleNote === b.styleNote &&
    JSON.stringify(a.referenceUploadIds) === JSON.stringify(b.referenceUploadIds)
  );
}

function fontToGoogleFamily(font: string): string | null {
  if (font === "system" || font === "Inter") return font === "Inter" ? "Inter" : null;
  return font;
}

function buildGoogleFontsHref(fonts: string[]): string | null {
  const families = Array.from(
    new Set(fonts.map(fontToGoogleFamily).filter((f): f is string => !!f)),
  );
  if (families.length === 0) return null;
  const query = families
    .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;600;700`)
    .join("&");
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}

/** whenDisabled: sentence shown instead of nothing while the brand kit is off for this company (/marke). */
export default function BrandKitCard({ whenDisabled }: { whenDisabled?: string } = {}) {
  const { toast } = useToast();
  const rqClient = useQueryClient();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const extractInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery<BrandResponse>({
    queryKey: ["/api/companies/me/brand"],
  });

  const { data: approvedData } = useQuery<ApprovedUpload[] | { uploads: ApprovedUpload[] }>({
    queryKey: ["/api/uploads/my-approved"],
    enabled: !!data?.enabled,
  });

  const approvedUploads: ApprovedUpload[] = Array.isArray(approvedData)
    ? approvedData
    : approvedData?.uploads ?? [];

  const [draft, setDraft] = useState<Draft>(DEFAULT_DRAFT);
  const [savedDraft, setSavedDraft] = useState<Draft>(DEFAULT_DRAFT);
  const [version, setVersion] = useState(0);
  const [unsaved, setUnsaved] = useState(false);
  const [pendingCandidate, setPendingCandidate] = useState<{ candidateId: string | number; url: string } | null>(
    null,
  );

  useEffect(() => {
    if (!data) return;
    const next = dtoToDraft(data.brand);
    setDraft(next);
    setSavedDraft(next);
    setVersion(data.version);
  }, [data]);

  const isDirty = useMemo(() => !draftsEqual(draft, savedDraft), [draft, savedDraft]);

  const fontsHref = useMemo(
    () => buildGoogleFontsHref([draft.fonts.heading, draft.fonts.body]),
    [draft.fonts.heading, draft.fonts.body],
  );

  useEffect(() => {
    if (!fontsHref) return;
    const id = "brand-kit-preview-fonts";
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = fontsHref;
  }, [fontsHref]);

  const invalidateBrand = () => {
    rqClient.invalidateQueries({ queryKey: ["/api/companies/me/brand"] });
  };

  const applyServerBrand = (brand: BrandKitDto, newVersion: number, opts?: { preserveDraft?: boolean }) => {
    const fresh = dtoToDraft(brand);
    setSavedDraft(fresh);
    setVersion(newVersion);
    if (!opts?.preserveDraft) {
      setDraft(fresh);
    }
  };

  const handleConflict = async (message?: string) => {
    try {
      const fresh = await apiRequest("GET", "/api/companies/me/brand") as BrandResponse;
      if (fresh.brand) {
        setSavedDraft(dtoToDraft(fresh.brand));
        setVersion(fresh.version);
      }
    } catch {
      // ignore — the toast still informs the user
    }
    setUnsaved(true);
    toast({
      title: "Marke wurde in der Zwischenzeit geändert",
      description: message || "Bitte erneut speichern.",
      variant: "destructive",
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PUT", "/api/companies/me/brand", {
        expectedVersion: version,
        logoPlacement: draft.logoPlacement,
        colors: draft.colors,
        fonts: draft.fonts,
        styleNote: draft.styleNote,
        referenceUploadIds: draft.referenceUploadIds,
      });
    },
    onSuccess: (res: { brand: BrandKitDto }) => {
      applyServerBrand(res.brand, res.brand.version);
      setUnsaved(false);
      toast({ title: "Marke gespeichert" });
    },
    onError: async (error: any) => {
      if (error?.status === 409) {
        await handleConflict();
        return;
      }
      toast({
        title: "Fehler",
        description: error?.data?.error || "Marke konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest(
        "DELETE",
        `/api/companies/me/brand?expectedVersion=${encodeURIComponent(String(version))}`,
      );
    },
    onSuccess: (res: { ok: true; version: number }) => {
      setDraft({ ...DEFAULT_DRAFT });
      setSavedDraft({ ...DEFAULT_DRAFT });
      setVersion(res.version);
      setUnsaved(false);
      invalidateBrand();
      toast({ title: "Marke zurückgesetzt" });
    },
    onError: async (error: any) => {
      if (error?.status === 409) {
        await handleConflict();
        return;
      }
      toast({
        title: "Fehler",
        description: error?.data?.error || "Zurücksetzen fehlgeschlagen.",
        variant: "destructive",
      });
    },
  });

  const logoUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("expectedVersion", String(version));
      return await apiUploadFiles("/api/companies/me/brand/logo", formData);
    },
    onSuccess: (res: { brand: BrandKitDto }) => {
      applyServerBrand(res.brand, res.brand.version, { preserveDraft: true });
      toast({
        title: "Logo aktualisiert",
        description: "Wird sofort übernommen.",
      });
    },
    onError: async (error: any) => {
      if (error?.status === 409) {
        await handleConflict();
        return;
      }
      toast({
        title: "Fehler",
        description: error?.data?.error || "Logo-Upload fehlgeschlagen.",
        variant: "destructive",
      });
    },
  });

  const extractFileMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return await apiUploadFiles("/api/companies/me/brand/extract", formData);
    },
    onSuccess: (res: { colors: { primary: string; secondary: string } | null; logoCandidate?: { candidateId: string | number; url: string } }) => {
      if (res.colors) {
        setDraft((d) => ({
          ...d,
          colors: { ...d.colors, primary: res.colors!.primary, secondary: res.colors!.secondary },
        }));
      }
      if (res.logoCandidate) {
        setPendingCandidate(res.logoCandidate);
      } else {
        toast({ title: "Farben übernommen" });
      }
    },
    onError: (error: any) => {
      if (error?.status === 429) {
        toast({
          title: "Extraktion ausgelastet",
          description: "Bitte gleich noch einmal versuchen.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Fehler",
        description: error?.data?.error || "Datei konnte nicht ausgewertet werden.",
        variant: "destructive",
      });
    },
  });

  const adoptLogoMutation = useMutation({
    mutationFn: async (candidateId: string | number) => {
      return await apiRequest("POST", "/api/companies/me/brand/logo/adopt", {
        candidateId,
        expectedVersion: version,
      });
    },
    onSuccess: (res: { brand: BrandKitDto }) => {
      applyServerBrand(res.brand, res.brand.version, { preserveDraft: true });
      setPendingCandidate(null);
      toast({
        title: "Logo übernommen",
        description: "Wird sofort übernommen.",
      });
    },
    onError: async (error: any) => {
      setPendingCandidate(null);
      if (error?.status === 409) {
        await handleConflict();
        return;
      }
      toast({
        title: "Fehler",
        description: error?.data?.error || "Logo konnte nicht übernommen werden.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.enabled) {
    // Profile (agencies): nothing, as before. /marke (hotels, `whenDisabled`): a sentence instead of an empty page.
    return whenDisabled ? (
      <Card data-testid="brand-kit-disabled">
        <CardContent className="py-6 text-sm text-muted-foreground">{whenDisabled}</CardContent>
      </Card>
    ) : null;
  }

  const handleLogoFile = (file: File | null) => {
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Nur PNG, WEBP oder JPEG erlaubt.",
        variant: "destructive",
      });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast({
        title: "Datei zu groß",
        description: "Maximal 5 MB.",
        variant: "destructive",
      });
      return;
    }
    logoUploadMutation.mutate(file);
  };

  const handleExtractFile = (file: File | null) => {
    if (!file) return;
    extractFileMutation.mutate(file);
  };

  const toggleReference = (id: number | string) => {
    setDraft((d) => {
      const exists = d.referenceUploadIds.includes(id);
      if (exists) {
        return { ...d, referenceUploadIds: d.referenceUploadIds.filter((x) => x !== id) };
      }
      if (d.referenceUploadIds.length >= MAX_REFERENCES) {
        toast({
          title: "Maximal 6 Referenzen",
          description: "Bitte entfernen Sie zuerst eine Referenz.",
          variant: "destructive",
        });
        return d;
      }
      return { ...d, referenceUploadIds: [...d.referenceUploadIds, id] };
    });
  };

  const previewHeadline = "Ihr Titel wirkt hier";

  return (
    <Card data-testid="card-brand-kit">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Palette className="h-5 w-5" />
          <CardTitle>Marke</CardTitle>
        </div>
        <CardDescription>
          Farben, Schriften und Logo für Ihre Beiträge.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {unsaved && (
          <Alert data-testid="alert-brand-unsaved" className="border-orange-300 bg-orange-50/50 dark:bg-orange-950/20">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800 dark:text-orange-200">
              Nicht gespeicherte Änderungen. Bitte erneut speichern.
            </AlertDescription>
          </Alert>
        )}
        {isDirty && !unsaved && (
          <Alert data-testid="alert-brand-dirty" className="border-orange-300 bg-orange-50/50 dark:bg-orange-950/20">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800 dark:text-orange-200">
              Sie haben ungespeicherte Änderungen. Klicken Sie auf "Speichern", um sie zu sichern.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* Logo */}
            <div className="space-y-2">
              <Label>Logo</Label>
              <div
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors flex flex-col items-center gap-2"
                onClick={() => logoInputRef.current?.click()}
                data-testid="dropzone-brand-logo"
              >
                {data.brand?.logoUrl ? (
                  <img
                    src={data.brand.logoUrl}
                    alt="Logo"
                    className="h-16 object-contain"
                    data-testid="img-brand-logo"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                )}
                {logoUploadMutation.isPending ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Wird hochgeladen...
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    PNG, WEBP oder JPEG, max. 5 MB. Wird sofort übernommen.
                  </p>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  className="hidden"
                  accept="image/png,image/webp,image/jpeg"
                  onChange={(e) => handleLogoFile(e.target.files?.[0] ?? null)}
                  data-testid="input-brand-logo"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="brand-logo-placement">Logo-Platzierung</Label>
              <Select
                value={draft.logoPlacement}
                onValueChange={(v) => setDraft((d) => ({ ...d, logoPlacement: v as Draft["logoPlacement"] }))}
              >
                <SelectTrigger id="brand-logo-placement" data-testid="select-logo-placement">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["all", "first", "none"] as const).map((p) => (
                    <SelectItem key={p} value={p}>
                      {LOGO_PLACEMENT_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand-color-primary">Hauptfarbe</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="brand-color-primary"
                    type="color"
                    value={draft.colors.primary}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, colors: { ...d.colors, primary: e.target.value } }))
                    }
                    className="h-9 w-9 rounded border cursor-pointer"
                    data-testid="input-color-primary-picker"
                  />
                  <Input
                    value={draft.colors.primary}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, colors: { ...d.colors, primary: e.target.value } }))
                    }
                    data-testid="input-color-primary-hex"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="brand-color-secondary">Zweitfarbe</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="brand-color-secondary"
                    type="color"
                    value={draft.colors.secondary}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, colors: { ...d.colors, secondary: e.target.value } }))
                    }
                    className="h-9 w-9 rounded border cursor-pointer"
                    data-testid="input-color-secondary-picker"
                  />
                  <Input
                    value={draft.colors.secondary}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, colors: { ...d.colors, secondary: e.target.value } }))
                    }
                    data-testid="input-color-secondary-hex"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Textfarbe</Label>
              <div className="flex gap-2">
                {TEXT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, colors: { ...d.colors, text: c } }))}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                      draft.colors.text === c ? "border-primary bg-primary/5" : "border-input"
                    }`}
                    data-testid={`button-text-color-${c === "#ffffff" ? "light" : "dark"}`}
                  >
                    <span
                      className="h-4 w-4 rounded-full border"
                      style={{ backgroundColor: c }}
                    />
                    {c === "#ffffff" ? "Heller Text" : "Dunkler Text"}
                    {draft.colors.text === c && <Check className="h-3 w-3" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand-font-heading">Überschrift</Label>
                <Select
                  value={draft.fonts.heading}
                  onValueChange={(v) => setDraft((d) => ({ ...d, fonts: { ...d.fonts, heading: v } }))}
                >
                  <SelectTrigger id="brand-font-heading" data-testid="select-font-heading">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BRAND_FONTS.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="brand-font-body">Text</Label>
                <Select
                  value={draft.fonts.body}
                  onValueChange={(v) => setDraft((d) => ({ ...d, fonts: { ...d.fonts, body: v } }))}
                >
                  <SelectTrigger id="brand-font-body" data-testid="select-font-body">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BRAND_FONTS.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="brand-style-note">Stil</Label>
                <span className="text-xs text-muted-foreground" data-testid="text-style-note-counter">
                  {draft.styleNote.length}/{MAX_STYLE_NOTE}
                </span>
              </div>
              <Textarea
                id="brand-style-note"
                value={draft.styleNote}
                maxLength={MAX_STYLE_NOTE}
                onChange={(e) => setDraft((d) => ({ ...d, styleNote: e.target.value }))}
                placeholder="z.B. reduziert, viel Weißraum, warme Töne"
                rows={3}
                data-testid="textarea-style-note"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  ref={extractInputRef}
                  type="file"
                  className="hidden"
                  accept="image/png,image/webp,image/jpeg"
                  onChange={(e) => handleExtractFile(e.target.files?.[0] ?? null)}
                  data-testid="input-brand-extract"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => extractInputRef.current?.click()}
                  disabled={extractFileMutation.isPending}
                  data-testid="button-extract-from-file"
                >
                  {extractFileMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Aus Datei übernehmen
                </Button>
              </div>

              {pendingCandidate && (
                <div className="border rounded-md p-3 flex items-center gap-3" data-testid="card-logo-candidate">
                  <img
                    src={pendingCandidate.url}
                    alt="Logo-Vorschlag"
                    className="h-12 w-12 object-contain border rounded bg-muted/30"
                  />
                  <div className="flex-1">
                    <p className="text-sm">Logo aus der Datei erkannt.</p>
                    <p className="text-xs text-muted-foreground">Als Logo übernehmen?</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => adoptLogoMutation.mutate(pendingCandidate.candidateId)}
                    disabled={adoptLogoMutation.isPending}
                    data-testid="button-adopt-logo-candidate"
                  >
                    {adoptLogoMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Übernehmen
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setPendingCandidate(null)}
                    data-testid="button-dismiss-logo-candidate"
                  >
                    Verwerfen
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Referenzen</Label>
                <span className="text-xs text-muted-foreground" data-testid="text-references-counter">
                  {draft.referenceUploadIds.length}/{MAX_REFERENCES}
                </span>
              </div>
              {approvedUploads.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Noch keine freigegebenen Fotos vorhanden.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {approvedUploads.map((u) => {
                    const selected = draft.referenceUploadIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => toggleReference(u.id)}
                        className={`relative aspect-square rounded-md overflow-hidden border-2 transition-colors ${
                          selected ? "border-primary" : "border-transparent"
                        }`}
                        data-testid={`button-reference-${u.id}`}
                      >
                        <img src={u.filePath} alt={u.message || "Referenz"} className="h-full w-full object-cover" />
                        {selected && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <Check className="h-5 w-5 text-white drop-shadow" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Vorschau */}
          <div className="space-y-2">
            <Label>Vorschau</Label>
            <div
              className="relative rounded-lg overflow-hidden border w-full max-w-sm mx-auto lg:mx-0"
              style={{
                aspectRatio: "3 / 4",
                background: `linear-gradient(160deg, ${draft.colors.primary} 0%, ${draft.colors.secondary} 100%)`,
              }}
              data-testid="preview-brand-kit"
            >
              <div className="absolute inset-0 flex flex-col justify-center px-6">
                <div
                  className="w-10 h-1 mb-3 rounded"
                  style={{ backgroundColor: draft.colors.primary }}
                />
                <p
                  className="text-xs uppercase tracking-wide mb-2"
                  style={{ color: draft.colors.text, opacity: 0.8 }}
                >
                  Kicker-Zeile
                </p>
                <div
                  className="w-8 mb-3"
                  style={{ borderBottom: `4px solid ${draft.colors.secondary}` }}
                />
                <p
                  className="text-2xl font-semibold leading-tight"
                  style={{
                    color: draft.colors.text,
                    fontFamily: draft.fonts.heading === "system" ? undefined : `'${draft.fonts.heading}', serif`,
                  }}
                >
                  {previewHeadline}
                </p>
              </div>
              {draft.logoPlacement !== "none" && data.brand?.logoUrl && (
                <img
                  src={data.brand.logoUrl}
                  alt="Logo"
                  className="absolute bottom-3 left-3 h-8 object-contain"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !isDirty}
            data-testid="button-save-brand"
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Speichern
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="outline" data-testid="button-reset-brand">
                <RotateCcw className="h-4 w-4 mr-2" />
                Zurücksetzen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Marke zurücksetzen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Farben, Schriften, Logo, Stil und Referenzen werden auf die Standardwerte
                  zurückgesetzt. Das kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => resetMutation.mutate()}
                  data-testid="button-confirm-reset-brand"
                >
                  Zurücksetzen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
